import type { AppBarProps } from '@mui/material/AppBar';
import type { ContainerProps } from '@mui/material/Container';
import type { Theme, SxProps, CSSObject, Breakpoint } from '@mui/material/styles';

import { varAlpha, mergeClasses } from 'minimal-shared/utils';

import AppBar from '@mui/material/AppBar';
import { styled } from '@mui/material/styles';
import Container from '@mui/material/Container';

import { layoutClasses } from './classes';

// ----------------------------------------------------------------------

export type FooterSectionProps = AppBarProps & {
  layoutQuery?: Breakpoint;
  slots?: {
    leftArea?: React.ReactNode;
    rightArea?: React.ReactNode;
    topArea?: React.ReactNode;
    centerArea?: React.ReactNode;
    bottomArea?: React.ReactNode;
  };
  slotProps?: {
    container?: ContainerProps;
    centerArea?: React.ComponentProps<'div'> & { sx?: SxProps<Theme> };
  };
};

export function FooterSection({
  sx,
  slots,
  slotProps,
  className,
  layoutQuery = 'md',
  ...other
}: FooterSectionProps) {
  const hasContainerContent = slots?.leftArea || slots?.centerArea || slots?.rightArea;

  return (
    <FooterRoot
      position="fixed"
      color="transparent"
      className={mergeClasses([layoutClasses.footer, className])}
      sx={sx}
      {...other}
    >
      {slots?.topArea}

      {hasContainerContent && (
        <FooterContainer layoutQuery={layoutQuery} {...slotProps?.container}>
          {slots?.leftArea}

          <FooterCenterArea {...slotProps?.centerArea}>{slots?.centerArea}</FooterCenterArea>

          {slots?.rightArea}
        </FooterContainer>
      )}

      {slots?.bottomArea}
    </FooterRoot>
  );
}

// ----------------------------------------------------------------------

const FooterRoot = styled(AppBar, {
  shouldForwardProp: (prop: string) => !['sx'].includes(prop),
})(({ theme }) => {
  const bgStyles: CSSObject = {
    ...theme.mixins.bgBlur({
      color: varAlpha(theme.vars.palette.background.defaultChannel, 0.8),
    }),
    content: '""',
    visibility: 'visible',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: -2,
  };

  const shadowStyles: CSSObject = {
    opacity: 0.48,
    content: '""',
    visibility: 'visible',
    position: 'absolute',
    transition: theme.transitions.create(['opacity', 'visibility'], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.shorter,
    }),
    left: 0,
    right: 0,
    top: 0,
    height: 24,
    margin: 'auto',
    borderRadius: '50%',
    width: `calc(100% - 48px)`,
    zIndex: -1,
    boxShadow: theme.vars.customShadows.z8,
  };

  return {
    top: 'auto',
    bottom: 0,
    // left: 0,
    // right: 0,
    zIndex: 'var(--layout-header-zIndex)',
    // Add safe area padding for mobile devices
    paddingBottom: 'var(--safe-area-bottom)',
    '&::before': bgStyles,
    '&::after': shadowStyles,
  };
});

const FooterContainer = styled(Container, {
  shouldForwardProp: (prop: string) => !['layoutQuery', 'sx'].includes(prop),
})<Pick<FooterSectionProps, 'layoutQuery'>>(({ layoutQuery = 'md', theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: 'var(--color)',
  height: 'var(--layout-header-mobile-height)',
  [theme.breakpoints.up(layoutQuery)]: { height: 'var(--layout-header-desktop-height)' },
}));

const FooterCenterArea = styled('div')(() => ({
  display: 'flex',
  flex: '1 1 auto',
  justifyContent: 'center',
}));
